import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { authAPI } from '../api'
import toast from 'react-hot-toast'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null)
  const [loading, setLoading] = useState(true)

  // ── Restore session ──
  useEffect(() => {
    const token = localStorage.getItem('sv_token')
    const stored = localStorage.getItem('sv_user')
    if (token && stored) {
      try { setUser(JSON.parse(stored)) }
      catch { /* ignore */ }
    }
    // Verify with server
    if (token) {
      authAPI.me()
        .then(({ data }) => {
          setUser(data.data)
          localStorage.setItem('sv_user', JSON.stringify(data.data))
        })
        .catch(() => {
          localStorage.removeItem('sv_token')
          localStorage.removeItem('sv_user')
          setUser(null)
        })
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [])

  const login = useCallback(async (credentials) => {
    const { data } = await authAPI.login(credentials)
    // API returns { success, message, data: <user>, token }
    const u = data.data ?? data.user
    localStorage.setItem('sv_token', data.token)
    localStorage.setItem('sv_user', JSON.stringify(u))
    setUser(u)
    return u
  }, [])

  const register = useCallback(async (payload) => {
    const { data } = await authAPI.register(payload)
    const u = data.data ?? data.user
    localStorage.setItem('sv_token', data.token)
    localStorage.setItem('sv_user', JSON.stringify(u))
    setUser(u)
    return u
  }, [])

  const logout = useCallback(async () => {
    try { await authAPI.logout() } catch { /* ignore */ }
    localStorage.removeItem('sv_token')
    localStorage.removeItem('sv_user')
    setUser(null)
    toast.success('Logged out successfully')
  }, [])

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      const updated = { ...prev, ...updates }
      localStorage.setItem('sv_user', JSON.stringify(updated))
      return updated
    })
  }, [])

  const isAdmin = user?.role === 'admin'
  const isStaff = user?.role === 'staff' || isAdmin

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, isAdmin, isStaff }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
